/**
 * Get the latest weighInDetails entry from macroData
 */
const SECOND_CALORIE_FLOOR_FACTOR = 19.5;

const getLBM = (weight, bodyFat) => {
  if (!Number.isFinite(weight) || !Number.isFinite(bodyFat)) {
    return null;
  }
  return weight - (weight * bodyFat) / 100;
};

const getSecondCalorieFloor = (user) => {
  const bodyFat = user.edited_body_fat ?? user.body_fat;
  const lbm = getLBM(user.weight, bodyFat);
  if (!Number.isFinite(lbm) || lbm <= 0) {
    return null;
  }
  return lbm * SECOND_CALORIE_FLOOR_FACTOR;
};

export const getLatestWeighInDetails = (macroData) => {
  if (!macroData || !macroData.weighInDetails) {
    return null;
  }
  
  const weighInDetails = macroData.weighInDetails;
  const dateKeys = Object.keys(weighInDetails).filter(key => {
    const entry = weighInDetails[key];
    return entry && entry.level === 2 && entry.source;
  });
  
  if (dateKeys.length === 0) {
    return null;
  }
  
  // Sort dates in descending order (latest first) - YYYY-MM-DD format sorts lexicographically
  dateKeys.sort((a, b) => b.localeCompare(a));
  
  return weighInDetails[dateKeys[0]];
};

/**
 * Get default macro values from user account
 */
export const getDefaultMacros = (user) => {
  const defaultProtein = user.last_protein ?? 150;
  const defaultCarbs = user.last_carbs ?? 200;
  const defaultFat = user.last_fat ?? 67;
  // Calculate calories from macros
  const defaultCalories = defaultProtein * 4 + defaultCarbs * 4 + defaultFat * 9;
  return { defaultProtein, defaultCarbs, defaultFat, defaultCalories };
};

/**
 * Get user's calorie goal (fallback to default calories if not set)
 */
export const getCalorieGoal = (user, getDefaultMacrosFn) => {
  return user.custom_calories || getDefaultMacrosFn(user).defaultCalories;
};

/**
 * Calculate dynamic calorie limits based on calorie goal
 * Lower limit: Decreases linearly from 60% at ≤ 1000 to 40% at ≥ 4000
 * Upper limit: Increases linearly from 200% at ≤ 2000 to 150% at ≥ 6000
 */
export const getCalorieLimits = (user, getDefaultMacrosFn) => {
  const calorieGoal = getCalorieGoal(user, getDefaultMacrosFn);
  
  // Calculate lower limit percentage
  let lowerPercentage;
  if (calorieGoal <= 1000) {
    lowerPercentage = 0.6; // 60%
  } else if (calorieGoal >= 4000) {
    lowerPercentage = 0.4; // 40%
  } else {
    // Linear interpolation between 60% at 1000 and 40% at 4000
    const slope = (0.4 - 0.6) / (4000 - 1000); // -0.2 / 3000 = -0.0000667
    lowerPercentage = 0.6 + slope * (calorieGoal - 1000);
  }
  
  // Calculate upper limit percentage
  let upperPercentage;
  if (calorieGoal <= 2000) {
    upperPercentage = 2.0; // 200%
  } else if (calorieGoal >= 6000) {
    upperPercentage = 1.5; // 150%
  } else {
    // Linear interpolation between 200% at 2000 and 150% at 6000
    const slope = (1.5 - 2.0) / (6000 - 2000); // -0.5 / 4000 = -0.000125
    upperPercentage = 2.0 + slope * (calorieGoal - 2000);
  }
  
  const lowerLimit = calorieGoal * lowerPercentage;
  const upperLimit = calorieGoal * upperPercentage;
  
  return { lowerLimit, upperLimit };
};

/**
 * Initialize weekly data from user account and last weigh-in date
 */
export const initializeWeeklyData = (user, lastWeighInDate, macroData, getDefaultMacrosFn) => {
  const { defaultCarbs, defaultFat, defaultCalories } = getDefaultMacrosFn(user);
  const userProtein = user.last_protein || user.protein || 150;

  // Get the weekday of lastWeighInDate (0 = Sunday, 1 = Monday, etc.)
  const startWeekday = lastWeighInDate.getDay();

  // Day names in order starting from Sunday
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Check if we have high_low_plan data from the latest weighInDetails
  const latestWeighInDetails = getLatestWeighInDetails(macroData);
  const highLowPlan = latestWeighInDetails?.source?.high_low_plan;

  // Create days array starting from the weekday of lastWeighInDate
  const days = [];
  for (let i = 0; i < 7; i++) {
    const dayIndex = (startWeekday + i) % 7;
    
    let dayCarbs = defaultCarbs;
    let dayFat = defaultFat;
    if (highLowPlan && Array.isArray(highLowPlan) && highLowPlan[i]) {
      dayCarbs = highLowPlan[i].carbs ?? defaultCarbs;
      dayFat = highLowPlan[i].fat ?? defaultFat;
    }
    const dayCalories = userProtein * 4 + dayCarbs * 4 + dayFat * 9;
    
    days.push({
      name: dayNames[dayIndex],
      calories: dayCalories,
      carbs: dayCarbs,
      fat: dayFat,
      locked: false,
    });
  }

  return { days };
};

/**
 * Get values for selected days - if multiple selected, calculate average/min/max
 */
export const getSelectedDaysValues = (selectedDays, weeklyData) => {
  if (selectedDays.length === 0) return null;
  if (selectedDays.length === 1) {
    return {
      calories: weeklyData.days[selectedDays[0]].calories,
      carbs: weeklyData.days[selectedDays[0]].carbs,
      fat: weeklyData.days[selectedDays[0]].fat,
    };
  }
  
  // Multiple days selected - calculate min, max, and average
  const calories = selectedDays.map(i => weeklyData.days[i].calories);
  const carbs = selectedDays.map(i => weeklyData.days[i].carbs);
  const fat = selectedDays.map(i => weeklyData.days[i].fat);
  
  return {
    calories: {
      min: Math.min(...calories),
      max: Math.max(...calories),
      avg: calories.reduce((a, b) => a + b, 0) / calories.length,
      values: calories,
    },
    carbs: {
      min: Math.min(...carbs),
      max: Math.max(...carbs),
      avg: carbs.reduce((a, b) => a + b, 0) / carbs.length,
      values: carbs,
    },
    fat: {
      min: Math.min(...fat),
      max: Math.max(...fat),
      avg: fat.reduce((a, b) => a + b, 0) / fat.length,
      values: fat,
    },
  };
};

/**
 * Calculate total weekly calories
 */
export const getTotalWeeklyCalories = (weeklyData) => {
  return weeklyData.days.reduce((total, day) => total + day.calories, 0);
};

/**
 * Calculate macro percentages for visualization
 */
export const getMacroPercentages = (day, userProtein) => {
  const proteinCalories = userProtein * 4;
  const carbsCalories = day.carbs * 4;
  const fatCalories = day.fat * 9;
  const totalCalories = proteinCalories + carbsCalories + fatCalories;

  if (totalCalories === 0) return { protein: 0, carbs: 0, fat: 0 };

  return {
    protein: Math.round((proteinCalories / totalCalories) * 100),
    carbs: Math.round((carbsCalories / totalCalories) * 100),
    fat: Math.round((fatCalories / totalCalories) * 100),
  };
};

/**
 * Check if a day's calories match the selected day's calories
 */
export const doesDayMatchSelectedCalories = (dayIndex, selectedDays, weeklyData) => {
  if (selectedDays.length === 0) return false;
  const selectedDayIndex = selectedDays[0];
  const selectedDayCalories = weeklyData.days[selectedDayIndex].calories;
  const dayCalories = weeklyData.days[dayIndex].calories;
  // Use Math.round to handle floating point precision issues
  return Math.round(selectedDayCalories) === Math.round(dayCalories);
};

export const getMacroFlexValue = (percentage) => {
  // Ensure minimum flex value for visibility
  return Math.max(0.1, percentage / 100);
};

const getReferenceCalories = (
  selectedDays,
  weeklyData,
  currentDay,
  selectedDaysValues,
  getDefaultMacrosFn,
  user
) => {
  const { defaultCalories } = getDefaultMacrosFn(user);
  if (selectedDays.length === 0) {
    return defaultCalories;
  }
  if (selectedDays.length === 1) {
    return currentDay?.calories ?? weeklyData?.days?.[selectedDays[0]]?.calories ?? defaultCalories;
  }
  return (
    weeklyData?.days?.[selectedDays[0]]?.calories ??
    selectedDaysValues?.calories?.avg ??
    defaultCalories
  );
};

export const getMinFat = (
  selectedDays,
  weeklyData,
  currentDay,
  selectedDaysValues,
  getDefaultMacrosFn,
  user
) => {
  const calories = getReferenceCalories(
    selectedDays,
    weeklyData,
    currentDay,
    selectedDaysValues,
    getDefaultMacrosFn,
    user
  );
  return Math.max(0, Math.round((15 * calories) / 900));
};

export const getMaxFat = (
  selectedDays,
  weeklyData,
  currentDay,
  selectedDaysValues,
  userProtein,
  getDefaultMacrosFn,
  user
) => {
  const calories = getReferenceCalories(
    selectedDays,
    weeklyData,
    currentDay,
    selectedDaysValues,
    getDefaultMacrosFn,
    user
  );
  const fatMin = getMinFat(
    selectedDays,
    weeklyData,
    currentDay,
    selectedDaysValues,
    getDefaultMacrosFn,
    user
  );
  return Math.max(fatMin, Math.round((calories - userProtein * 4) / 9));
};

export const getMinCarbs = (
  selectedDays,
  weeklyData,
  currentDay,
  selectedDaysValues,
  userProtein,
  getDefaultMacrosFn,
  user
) => {
  const calories = getReferenceCalories(
    selectedDays,
    weeklyData,
    currentDay,
    selectedDaysValues,
    getDefaultMacrosFn,
    user
  );
  const fatMax = getMaxFat(
    selectedDays,
    weeklyData,
    currentDay,
    selectedDaysValues,
    userProtein,
    getDefaultMacrosFn,
    user
  );
  return Math.max(0, Math.round((calories - fatMax * 9 - userProtein * 4) / 4));
};

export const getMaxCarbs = (
  selectedDays,
  weeklyData,
  currentDay,
  selectedDaysValues,
  userProtein,
  getDefaultMacrosFn,
  user
) => {
  const calories = getReferenceCalories(
    selectedDays,
    weeklyData,
    currentDay,
    selectedDaysValues,
    getDefaultMacrosFn,
    user
  );
  const fatMin = getMinFat(
    selectedDays,
    weeklyData,
    currentDay,
    selectedDaysValues,
    getDefaultMacrosFn,
    user
  );
  const carbsMin = getMinCarbs(
    selectedDays,
    weeklyData,
    currentDay,
    selectedDaysValues,
    userProtein,
    getDefaultMacrosFn,
    user
  );
  return Math.max(carbsMin, Math.round((calories - fatMin * 9 - userProtein * 4) / 4));
};

/**
 * Get slider value - use halfway point if multiple days with different values
 */
export const getSliderValue = (type, selectedDays, weeklyData, selectedDaysValues, getDefaultMacrosFn, user) => {
  if (selectedDays.length === 0) {
    // No days selected - return default value based on type
    const { defaultCarbs, defaultFat, defaultCalories } = getDefaultMacrosFn(user);
    if (type === "calories") {
      return defaultCalories;
    } else if (type === "carbs") {
      return defaultCarbs;
    } else if (type === "fat") {
      return defaultFat;
    }
    return 0;
  }
  
  if (selectedDays.length === 1) {
    const day = weeklyData.days[selectedDays[0]];
    return type === "calories" ? day.calories : day[type];
  }
  
  // Multiple days selected - use halfway point between min and max
  const values = selectedDaysValues[type];
  if (values.min === values.max) {
    return values.avg; // All same value
  }
  // Different values - use halfway point
  return (values.min + values.max) / 2;
};

/**
 * min = effective calorie floor
 * max = min(dynamic upper limit, redistribution max)
 */
export const getCalorieSliderBounds = (
  selectedDays,
  weeklyData,
  getCalorieLimitsFn,
  user,
  getDefaultMacrosFn
) => {
  const { lowerLimit, upperLimit } = getCalorieLimitsFn(user, getDefaultMacrosFn);
  const calculatedCalorieFloor = getSecondCalorieFloor(user);
  const effectiveCalorieFloor = user.custom_calorie_floor || calculatedCalorieFloor || lowerLimit;

  if (selectedDays.length === 0 || !weeklyData?.days?.length) {
    return {
      min: effectiveCalorieFloor,
      max: upperLimit,
    };
  }

  const selectedSet = new Set(selectedDays);
  const unlockedSelected = selectedDays.filter(
    (index) => weeklyData.days[index] && !weeklyData.days[index].locked
  );

  if (unlockedSelected.length === 0) {
    return {
      min: effectiveCalorieFloor,
      max: upperLimit,
    };
  }

  const selectedCalories = weeklyData.days[unlockedSelected[0]].calories;
  const availableCalories = weeklyData.days.reduce((total, day, index) => {
    if (day.locked || selectedSet.has(index)) {
      return total;
    }
    return total + Math.max(0, day.calories - effectiveCalorieFloor);
  }, 0);

  const redistributionMax =
    selectedCalories + availableCalories / unlockedSelected.length;
  const max = Math.min(upperLimit, redistributionMax);

  return {
    min: effectiveCalorieFloor,
    max: Math.max(effectiveCalorieFloor, max),
  };
};

/**
 * Validate input values using the same limit logic as sliders
 */
export const validateInputs = (calories, carbs, fat, userProtein, getCalorieLimitsFn, user, getDefaultMacrosFn, selectedDays, weeklyData, currentDay, selectedDaysValues) => {
  const errors = [];
  const { min: calorieMin, max: calorieMax } = getCalorieSliderBounds(
    selectedDays,
    weeklyData,
    getCalorieLimitsFn,
    user,
    getDefaultMacrosFn
  );

  // Validate calories
  if (calories < calorieMin) {
    errors.push(`Calories must be at least ${Math.round(calorieMin)}.`);
  }
  if (calories > calorieMax) {
    errors.push(`Calories must not exceed ${Math.round(calorieMax)}.`);
  }

  // Calculate min/max carbs using the same logic as sliders
  const minCarbs = getMinCarbs(selectedDays, weeklyData, currentDay, selectedDaysValues, userProtein, getDefaultMacrosFn, user);
  const maxCarbs = getMaxCarbs(selectedDays, weeklyData, currentDay, selectedDaysValues, userProtein, getDefaultMacrosFn, user);
  
  if (carbs < minCarbs) {
    errors.push(`Carbs must be at least ${Math.round(minCarbs)}g.`);
  }
  if (carbs > maxCarbs) {
    errors.push(`Carbs must not exceed ${Math.round(maxCarbs)}g.`);
  }

  // Calculate min/max fat using the same logic as sliders
  const minFat = getMinFat(selectedDays, weeklyData, currentDay, selectedDaysValues, getDefaultMacrosFn, user);
  const maxFat = getMaxFat(selectedDays, weeklyData, currentDay, selectedDaysValues, userProtein, getDefaultMacrosFn, user);
  
  if (fat < minFat) {
    errors.push(`Fat must be at least ${Math.round(minFat)}g.`);
  }
  if (fat > maxFat) {
    errors.push(`Fat must not exceed ${Math.round(maxFat)}g.`);
  }

  return errors;
};

/**
 * Check if a calorie change would cause any day to hit limits
 */
export const canChangeCalories = (
  newCalories,
  selectedDays,
  weeklyData,
  getCalorieLimitsFn,
  user,
  getDefaultMacrosFn,
  calorieBoundsOverride = null
) => {
  const computedBounds = getCalorieSliderBounds(
    selectedDays,
    weeklyData,
    getCalorieLimitsFn,
    user,
    getDefaultMacrosFn
  );
  const calorieMin = calorieBoundsOverride?.min ?? computedBounds.min;
  const calorieMax = calorieBoundsOverride?.max ?? computedBounds.max;
  
  // Check selected days are within limits
  if (newCalories < calorieMin || newCalories > calorieMax) {
    return false;
  }

  // Calculate total current calories for all selected days
  const totalCurrentCalories = selectedDays.reduce(
    (sum, index) => sum + weeklyData.days[index].calories,
    0
  );
  const totalNewCalories = newCalories * selectedDays.length;
  const totalCalorieDifference = totalNewCalories - totalCurrentCalories;

  // If no difference, change is valid
  if (totalCalorieDifference === 0) return true;

  // Get unlocked days that will receive the adjustment
  const unlockedDays = weeklyData.days.filter(
    (day, index) => !selectedDays.includes(index) && !day.locked
  );

  if (unlockedDays.length === 0) {
    // No unlocked days to redistribute to - already checked limits above
    return true;
  }

  // Calculate how much to adjust each unlocked day (keep decimals for accuracy)
  const adjustmentPerDay = -totalCalorieDifference / unlockedDays.length;

  // Check if any unlocked day would hit limits after adjustment
  for (let i = 0; i < weeklyData.days.length; i++) {
    if (!selectedDays.includes(i) && !weeklyData.days[i].locked) {
      const newDayCalories = weeklyData.days[i].calories + adjustmentPerDay;
      if (newDayCalories < calorieMin) {
        return false;
      }
    }
  }

  // Handle any remaining difference due to floating point precision
  const totalAdjustment = adjustmentPerDay * unlockedDays.length;
  const remainingDifference = -totalCalorieDifference - totalAdjustment;

  if (remainingDifference !== 0 && unlockedDays.length > 0) {
    // Find first unlocked day that would receive remaining difference
    const firstUnlockedIndex = weeklyData.days.findIndex(
      (day, index) => !selectedDays.includes(index) && !day.locked
    );
    if (firstUnlockedIndex !== -1) {
      const finalCalories = weeklyData.days[firstUnlockedIndex].calories +
        adjustmentPerDay + remainingDifference;
      if (finalCalories < calorieMin) {
        return false;
      }
    }
  }

  return true;
};
